import { Alert, FormControl } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { shopsApi } from 'api';
import { FormikCheckbox, FormikNumericField, HelpTooltip } from 'components';
import { QueryKey } from 'enums';
import { useCurrencies, useMeta } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { formatUtils } from 'utils';

type Props = {
  shopId?: string;
  assetCurrencyId?: string;
  /** The setting as saved: the limit status is only meaningful for it, not for unsaved form values */
  savedNegativeProfitAllowed?: boolean;
  /** Part of the query key, so that saving a new limit refetches the status */
  savedNegativeProfitDailyLossLimit?: number;
};

// Shown to admins and tech operators only
export const NegativeProfitSetting: React.FC<Props> = ({
  shopId,
  assetCurrencyId,
  savedNegativeProfitAllowed,
  savedNegativeProfitDailyLossLimit,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { getAssetCurrencyCode } = useCurrencies();
  const { isNegativeProfitAllowanceEnabled } = useMeta();
  const { values } = useFormikContext<{ negativeProfitAllowed?: boolean }>();

  const { data: status } = useQuery(
    [
      QueryKey.ShopNegativeProfitStatus,
      shopId,
      savedNegativeProfitAllowed,
      savedNegativeProfitDailyLossLimit,
    ],
    () => shopsApi.getNegativeProfitStatus(shopId as string),
    {
      enabled:
        isNegativeProfitAllowanceEnabled &&
        !!shopId &&
        !!savedNegativeProfitAllowed,
    },
  );

  if (!isNegativeProfitAllowanceEnabled) {
    return null;
  }

  const currency = assetCurrencyId ? getAssetCurrencyCode(assetCurrencyId) : '';
  const isLimitReached =
    savedNegativeProfitAllowed && status?.enabled && status.limitReached;

  return (
    <>
      <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
        <FormikCheckbox
          name="negativeProfitAllowed"
          label={t('fields.negative_profit_allowed')}
          sx={{ marginRight: 0 }}
        />
        <HelpTooltip title={t('fields.negative_profit_allowed_helper')} />
      </FormControl>
      {values.negativeProfitAllowed && (
        <FormikNumericField
          name="negativeProfitDailyLossLimit"
          label={t('fields.negative_profit_daily_loss_limit')}
          suffix={currency}
          allowNegative={false}
          fullWidth
        />
      )}
      {isLimitReached && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {status.dailyResult === null
            ? t('fields.negative_profit_limit_unknown')
            : t('fields.negative_profit_limit_reached', {
                dailyResult: formatUtils.formatNumber(status.dailyResult),
                limit: formatUtils.formatNumber(status.limit),
                currency,
              })}
        </Alert>
      )}
    </>
  );
};
